import React from 'react';
import { useTranslation } from 'react-i18next';
import ShipmentCard from './ShipmentCard';
import type { ShipmentListItemView } from '../../api/generated/models/ShipmentListItemView';
import type { ShipmentStatusRequest } from '../../api/generated/models/ShipmentStatusRequest';

interface ShipmentColumnProps {
  status: string;
  shipments: ShipmentListItemView[];
  canWrite: boolean;
  draggingId: string | null;
  isDragOver: boolean;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDragEnter: (status: string) => void;
  onDragLeave: (status: string) => void;
  onDrop: (status: string, id: string) => void;
  onChangeStatus: (id: string, status: ShipmentStatusRequest.executionStatus) => void;
  onArchive: (id: string) => void;
}

const ShipmentColumn: React.FC<ShipmentColumnProps> = ({
  status,
  shipments,
  canWrite,
  draggingId,
  isDragOver,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDrop,
  onChangeStatus,
  onArchive,
}) => {
  const { t } = useTranslation();

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    onDragEnter(status);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    onDragLeave(status);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    onDrop(status, id);
  };

  return (
    <section className="shipment-column">
      <header className="shipment-column__header">
        <h2 className="shipment-column__title">{t(`shipment_status_${status}`)}</h2>
        <span className="shipment-column__count">{shipments.length}</span>
      </header>
      <div
        className={`shipment-column__body${isDragOver ? ' shipment-column__body--drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {shipments.map((s) => (
          <ShipmentCard
            key={s.id}
            shipment={s}
            canWrite={canWrite}
            draggable={canWrite}
            isDragging={draggingId === s.id}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onChangeStatus={onChangeStatus}
            onArchive={onArchive}
          />
        ))}
      </div>
    </section>
  );
};

export default ShipmentColumn;
